import {
  isDefinedString,
  isNullOrUndefined,
  isPositiveNumber,
  reduce
} from "et3-type-guards";

import { E_KHATTABAT_ONE_O_TWO_MESSAGE_HISTORY_EVENT_TYPE } from "../constants/enums/khattabat-one-o-two-message-history-event-type.enum";

import { areDateStringsEqual, compareDateStrings } from "./et3-date-helper";

import {
  TKhattabatOneOTwoHistoryRow,
  TKhattabatOneOTwoMessageHistoryEvent
} from "../types/khattabat-one-o-two-message.type";
import { Ti18nKeys } from "../constants/i18n";

// Two history rows are "the same timestamp" only when both are real dates
// and resolve to the exact same instant - an empty deactivatedOn (row still
// active) must never accidentally match an empty createdOn.
const areSameHistoryTimestamp = (
  firstTimestamp: string,
  secondTimestamp: string
): boolean => {
  if (!isDefinedString(firstTimestamp) || !isDefinedString(secondTimestamp)) {
    return false;
  }

  return areDateStringsEqual(firstTimestamp, secondTimestamp);
};

// Reaction/hide rows are stored as an activation ledger (one row per
// active/deactivated span), not as discrete events. This turns that ledger
// into a flat, display-ready timeline for the Histories popup: each row can
// produce an ADDED/CHANGED event (when it starts) and a REMOVED event (when
// it ends), grouped per actor (sender/hider) and sorted newest first.
export const deriveKhattabatOneOTwoHistoryEvents = <
  THistoryRow extends TKhattabatOneOTwoHistoryRow
>({
  historyRows,
  getEventLabel,
  getHistoryGroupKey,
  getRecipientUUID
}: {
  historyRows: THistoryRow[];

  getEventLabel: (historyRow: THistoryRow) => string;

  getHistoryGroupKey: (historyRow: THistoryRow) => string;

  getRecipientUUID?: (historyRow: THistoryRow) => string;
}): TKhattabatOneOTwoMessageHistoryEvent[] => {
  // Group rows per actor first, since "was this row created in place of the
  // previous one" only makes sense when comparing rows from the same actor.
  const groupedHistoryRows = historyRows.reduce<Record<string, THistoryRow[]>>(
    (historyRowsByActor, historyRow) => {
      const historyGroupKey = getHistoryGroupKey(historyRow);

      if (isNullOrUndefined(historyRowsByActor[historyGroupKey])) {
        historyRowsByActor[historyGroupKey] = [];
      }

      historyRowsByActor[historyGroupKey].push(historyRow);

      return historyRowsByActor;
    },
    {}
  );

  const historyEvents: TKhattabatOneOTwoMessageHistoryEvent[] = [];

  Object.entries(groupedHistoryRows).forEach(
    ([historyGroupKey, actorHistoryRows]) => {
      // Oldest first within the actor's own rows, so "previous"/"next" below
      // reflect the actual chronological order the rows happened in.
      const sortedActorHistoryRows = [...actorHistoryRows].sort(
        (firstRow, secondRow) =>
          compareDateStrings(firstRow.createdOn, secondRow.createdOn)
      );

      sortedActorHistoryRows.forEach((historyRow, historyRowIndex) => {
        const previousHistoryRow = sortedActorHistoryRows[historyRowIndex - 1];

        // A row created at the exact moment the previous one deactivated is
        // a replacement (e.g. reaction swapped from one emoji to another),
        // so it reads as CHANGED rather than a brand-new ADDED event.
        const wasCreatedInPlaceOfPreviousRow =
          !isNullOrUndefined(previousHistoryRow) &&
          areSameHistoryTimestamp(
            previousHistoryRow.deactivatedOn,
            historyRow.createdOn
          );

        historyEvents.push({
          eventType: wasCreatedInPlaceOfPreviousRow
            ? E_KHATTABAT_ONE_O_TWO_MESSAGE_HISTORY_EVENT_TYPE.CHANGED
            : E_KHATTABAT_ONE_O_TWO_MESSAGE_HISTORY_EVENT_TYPE.ADDED,

          label: getEventLabel(historyRow),

          occurredOn: historyRow.createdOn,

          actorUUID: historyGroupKey,

          recipientUUID: reduce(() => getRecipientUUID?.(historyRow), ""),

          isCurrent: historyRow.isActive === 1
        });

        const nextHistoryRow = sortedActorHistoryRows[historyRowIndex + 1];

        // If this row's deactivation lines up with the next row's creation,
        // that deactivation is just the CHANGED transition above (already
        // represented by the next row's own ADDED/CHANGED event), so it must
        // not also be emitted here as a separate REMOVED event.
        const wasReplacedByNextRow =
          !isNullOrUndefined(nextHistoryRow) &&
          areSameHistoryTimestamp(
            historyRow.deactivatedOn,
            nextHistoryRow.createdOn
          );

        if (
          !isPositiveNumber(historyRow.isActive) &&
          isDefinedString(historyRow.deactivatedOn) &&
          !wasReplacedByNextRow
        ) {
          historyEvents.push({
            eventType: E_KHATTABAT_ONE_O_TWO_MESSAGE_HISTORY_EVENT_TYPE.REMOVED,

            label: getEventLabel(historyRow),

            occurredOn: historyRow.deactivatedOn,

            actorUUID: historyGroupKey,

            recipientUUID: reduce(() => getRecipientUUID?.(historyRow), ""),

            isCurrent: false
          });
        }
      });
    }
  );

  // Newest first, matching the order the Histories popup renders in.
  return historyEvents.sort((firstEvent, secondEvent) =>
    compareDateStrings(secondEvent.occurredOn, firstEvent.occurredOn)
  );
};

export const getKhattabatOneOTwoHistoryEventTypeI18nKey = (
  eventType: string
): Ti18nKeys => {
  if (eventType === E_KHATTABAT_ONE_O_TWO_MESSAGE_HISTORY_EVENT_TYPE.ADDED) {
    return "KHATTABAT_ONE_O_TWO_HISTORY_EVENT_ADDED";
  }
  if (eventType === E_KHATTABAT_ONE_O_TWO_MESSAGE_HISTORY_EVENT_TYPE.CHANGED) {
    return "KHATTABAT_ONE_O_TWO_HISTORY_EVENT_UPDATED";
  }
  if (eventType === E_KHATTABAT_ONE_O_TWO_MESSAGE_HISTORY_EVENT_TYPE.REMOVED) {
    return "KHATTABAT_ONE_O_TWO_HISTORY_EVENT_REMOVED";
  }
  return "EMPTY_STRING";
};
